import { useEffect, useState } from 'react';
import { useAppSelector } from 'rtk/query/hooks';
import type {
  TFormSliderEvaluationSummary,
  TOpenEvaluationSummary,
  TSelectEvaluationSummary,
} from 'types/evaluation.types';
import { EQuestionType } from '@hubertai/screening-question-types';
import { useQuestion } from '../useQuestion';
import evaluateDatepicker from './strategies/evaluateDatepicker';
import evaluateForm from './strategies/evaluateForm';
import evaluateMultipleSelect from './strategies/evaluateMultipleSelect';
import evaluateOpen from './strategies/evaluateOpen';
import evaluateSingleSelect from './strategies/evaluateSingleSelect';

const evaluationStrategies = {
  [EQuestionType.SingleSelect]: evaluateSingleSelect,
  [EQuestionType.MultipleSelect]: evaluateMultipleSelect,
  [EQuestionType.Open]: evaluateOpen,
  [EQuestionType.Form]: evaluateForm,
  [EQuestionType.DatePicker]: evaluateDatepicker,
};

export function useQuestionEvaluation(nodeId: string): {
  evaluation: TSelectEvaluationSummary | TOpenEvaluationSummary | TFormSliderEvaluationSummary | undefined;
  isDefault: boolean;
  isLoading: boolean;
} {
  const { questionConfig, question, language, isLoading: questionIsLoading, missingLanguage } = useQuestion(nodeId);
  const nodeEvaluation = useAppSelector((state) => state.Template?.present?.c!.evaluation[nodeId]);
  const categories = useAppSelector((state) => state.Template?.present?.c!.evaluation.scoring_categories);

  // console.log(`useQuestionEvaluation categories ${nodeId}`, categories)

  const [evaluation, setEvaluation] = useState<
    TSelectEvaluationSummary | TOpenEvaluationSummary | TFormSliderEvaluationSummary
  >();
  const [isDefault] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (questionIsLoading || !questionConfig) {
      // Wait for question to load
      return;
    }

    if (missingLanguage) {
      // Return nothing if language doesn't exist yet
      return;
    }

    if (
      ![
        EQuestionType.SingleSelect,
        EQuestionType.MultipleSelect,
        EQuestionType.Open,
        EQuestionType.Form,
        EQuestionType.DatePicker,
      ].includes(questionConfig.type)
    ) {
      setIsLoading(false);
      return;
    }

    // @ts-ignore
    const evaluationFn = evaluationStrategies[questionConfig.type];
    if (evaluationFn) {
      const result = evaluationFn(question, language, nodeEvaluation, categories);
      setEvaluation(result);
    }
    setIsLoading(false);
  }, [nodeEvaluation, categories, language, questionConfig, question, questionIsLoading]);

  return {
    evaluation,
    isDefault,
    isLoading,
  };
}
